import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Memory {
  id: string;
  key: string;
  value: any;
  memory_type: string;
  confidence: number;
  source: string;
  access_count: number;
  created_at: string;
  updated_at: string;
}

interface UserModel {
  persona: Record<string, any>;
  goals: any[];
  patterns: Record<string, any>;
  preferences: Record<string, any>;
  pain_points: any[];
  active_hours: Record<string, number>;
  communication_style: string;
  last_analyzed_at: string | null;
}

interface InteractionLog {
  interaction_type: 'chat' | 'action' | 'approval' | 'scan' | 'bulk_action';
  input: any;
  output?: any;
  context?: any;
  outcome?: 'success' | 'partial' | 'failed' | 'pending';
  feedback?: 'positive' | 'negative' | 'neutral';
  latency_ms?: number;
}

interface FullContext {
  memories: Memory[];
  userModel: UserModel | null;
  recentInteractions: any[];
  preferences: Record<string, any>;
}

export function useAgentMemory() {
  const { user } = useAuth();

  // Store a memory
  const remember = useCallback(async (
    key: string,
    value: any,
    type: 'preference' | 'fact' | 'pattern' | 'context' = 'fact',
    source: 'explicit' | 'inferred' | 'learned' = 'explicit'
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_memory')
        .upsert({
          user_id: user.id,
          key,
          value: typeof value === 'object' ? value : { value },
          memory_type: type,
          source,
          confidence: source === 'explicit' ? 1.0 : 0.8,
          updated_at: new Date().toISOString(),
          accessed_at: new Date().toISOString(),
        }, { onConflict: 'user_id,key' });

      return !error;
    } catch {
      return false;
    }
  }, [user]);

  // Retrieve a memory and update access count
  const recall = useCallback(async (key: string): Promise<any | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('user_memory')
        .select('*')
        .eq('user_id', user.id)
        .eq('key', key)
        .single();

      if (error || !data) return null;

      // Update access tracking
      await supabase
        .from('user_memory')
        .update({
          accessed_at: new Date().toISOString(),
          access_count: (data.access_count || 0) + 1,
        })
        .eq('id', data.id);

      return data.value;
    } catch {
      return null;
    }
  }, [user]);

  // Get all memories for a user
  const getAllMemories = useCallback(async (limit = 50): Promise<Memory[]> => {
    if (!user) return [];

    try {
      const { data } = await supabase
        .from('user_memory')
        .select('*')
        .eq('user_id', user.id)
        .order('access_count', { ascending: false })
        .limit(limit);

      return (data || []) as Memory[];
    } catch {
      return [];
    }
  }, [user]);

  // Get user model
  const getUserModel = useCallback(async (): Promise<UserModel | null> => {
    if (!user) return null;

    try {
      const { data } = await supabase
        .from('user_model')
        .select('*')
        .eq('user_id', user.id)
        .single();

      return data as UserModel | null;
    } catch {
      return null;
    }
  }, [user]);

  // Get recent interaction history
  const getRecentInteractions = useCallback(async (limit = 10): Promise<any[]> => {
    if (!user) return [];

    try {
      const { data } = await supabase
        .from('interaction_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      return data || [];
    } catch {
      return [];
    }
  }, [user]);

  // Log an interaction
  const logInteraction = useCallback(async (log: InteractionLog): Promise<string | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('interaction_history')
        .insert({
          user_id: user.id,
          session_id: getSessionId(),
          ...log,
        })
        .select('id')
        .single();

      return error ? null : data.id;
    } catch {
      return null;
    }
  }, [user]);

  // Update interaction outcome
  const updateOutcome = useCallback(async (
    interactionId: string,
    outcome: 'success' | 'partial' | 'failed',
    feedback?: 'positive' | 'negative' | 'neutral'
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('interaction_history')
        .update({ outcome, feedback })
        .eq('id', interactionId)
        .eq('user_id', user.id);

      // Trigger learning from outcome
      if (!error) {
        supabase.functions.invoke('learn-from-outcomes', {
          body: { user_id: user.id, interaction_id: interactionId, outcome, feedback },
        }).catch(() => {}); // Fire and forget
      }

      return !error;
    } catch {
      return false;
    }
  }, [user]);

  // Get full context for AI calls
  const getFullContext = useCallback(async (): Promise<FullContext> => {
    if (!user) {
      return { memories: [], userModel: null, recentInteractions: [], preferences: {} };
    }

    try {
      const [memories, userModel, recentInteractions] = await Promise.all([
        getAllMemories(20),
        getUserModel(),
        getRecentInteractions(10),
      ]);

      // Extract preferences from memories
      const preferences: Record<string, any> = {};
      for (const memory of memories.filter(m => m.memory_type === 'preference')) {
        preferences[memory.key] = memory.value;
      }

      return {
        memories,
        userModel,
        recentInteractions,
        preferences,
      };
    } catch {
      return { memories: [], userModel: null, recentInteractions: [], preferences: {} };
    }
  }, [user, getAllMemories, getUserModel, getRecentInteractions]);

  // Forget a specific memory
  const forget = useCallback(async (key: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_memory')
        .delete()
        .eq('user_id', user.id)
        .eq('key', key);

      return !error;
    } catch {
      return false;
    }
  }, [user]);

  return {
    remember,
    recall,
    forget,
    getAllMemories,
    getUserModel,
    getRecentInteractions,
    logInteraction,
    updateOutcome,
    getFullContext,
  };
}

// Helper to get/create session ID
function getSessionId(): string {
  const key = 'sentinel_session_id';
  let sessionId = sessionStorage.getItem(key);
  
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  
  return sessionId;
}